











































































































import { defineComponent, PropType, computed, useRoute, useRouter, useContext, ref } from '@nuxtjs/composition-api';
import AppIcon from '~/components/partials/App/AppIcon.vue';
import AppImage from '~/components/partials/App/AppImage.vue';
import AppLink from '~/components/partials/App/AppLink.vue';
import AppRectButton from '~/components/partials/App/AppRectButton.vue';
import FormRadioGroup from '~/components/partials/Form/FormRadioGroup.vue';
import { useAlert } from '~/composables/useAlert';
import { usePropSync } from '~/composables/usePropSync';
import { useStore } from '~/composables/useStore';
import { COOKIE_KEY } from '~/constants';
import { BLOG_SEARCH_ALL_BLOG_OPTION } from '~/constants/blog';
import { url, formatZipCode } from '~/utils';
import { ECSchema } from '~/types/api';
import { PastArticle } from '~/types/modules/blog';
import { StaffDetail } from '~/types/modules/staff';

export default defineComponent({
  components: {
    AppIcon,
    AppImage,
    AppLink,
    AppRectButton,
    FormRadioGroup,
  },
  props: {
    shopDetail: {
      type: Object as PropType<ECSchema<'saleslocation.ShowResponse'>>,
      required: true,
    },
    staffShopId: {
      type: String,
      required: true,
    },
    pastArticleList: {
      type: Array as PropType<PastArticle[]>,
      required: true,
    },
    staffList: {
      type: Array as PropType<StaffDetail[]>,
      required: true,
    },
    selectedYearOption: {
      type: String,
      default: '',
      required: false,
    },
    selectedMonthOption: {
      type: String,
      default: '',
      required: false,
    },
    selectedAllBlogOption: {
      type: Boolean,
      default: false,
    },
    isFromDetailPage: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const { i18n, $cookies } = useContext();
    const { openAlert } = useAlert();
    const isShowAll = ref(false);
    const authenticated = computed(() => store.state.auth.authenticated);
    const { syncedSelectedAllBlogOption, syncedSelectedYearOption, syncedSelectedMonthOption } = usePropSync(
      props,
      'selectedAllBlogOption',
      'selectedYearOption',
      'selectedMonthOption'
    );

    const shopPostNum = computed(() => {
      if (props.shopDetail?.post_no) {
        return '〒' + formatZipCode(props.shopDetail.post_no) + ' ';
      }
    });

    /** 店舗住所を結合して返す */
    const shopAddress = computed(() => {
      if (
        props.shopDetail.state_name &&
        props.shopDetail.city_name &&
        props.shopDetail.address_name1 &&
        props.shopDetail.address_name2
      ) {
        return (
          props.shopDetail.state_name +
          props.shopDetail.city_name +
          props.shopDetail.address_name1 +
          props.shopDetail.address_name2
        );
      }
    });

    const slicedStaffList = computed(() => {
      return props.staffList.slice(0, 3);
    });

    const applyAllBlog = async (value: boolean) => {
      // 詳細画面の場合、ブログTOP画面にリダイレクトする
      if (props.isFromDetailPage) {
        await router.push(url('BLOG_TOP', { permalink: route.value.params.permalink }));
        return;
      }
      syncedSelectedAllBlogOption.value = value;
      syncedSelectedYearOption.value = '';
      syncedSelectedMonthOption.value = '';

      emit('apply-filter');
    };

    const applyYearFilter = async (year: string) => {
      // 詳細画面の場合、ブログTOP画面にリダイレクトする
      if (props.isFromDetailPage) {
        await router.push({
          path: url('BLOG_TOP', { permalink: route.value.params.permalink }),
          query: { _year: year },
        });
        return;
      }

      syncedSelectedYearOption.value = year;
      syncedSelectedMonthOption.value = '';
      syncedSelectedAllBlogOption.value = false;

      emit('apply-filter');
    };

    const applyMonthFilter = async (year: string, month: string) => {
      // 詳細画面の場合、ブログTOP画面にリダイレクトする
      if (props.isFromDetailPage) {
        await router.push({
          path: url('BLOG_TOP', { permalink: route.value.params.permalink }),
          query: { _year: year, _month: month },
        });
        return;
      }
      syncedSelectedYearOption.value = year;
      syncedSelectedMonthOption.value = month;
      syncedSelectedAllBlogOption.value = false;

      emit('apply-filter');
    };

    const openLoginDialog = () => {
      openAlert({
        title: 'ログインしてください',
        message: 'マイショップをご利用するにはログインしてください',
        buttons: [
          {
            text: i18n.t('Common.button.cancel').toString(),
            style: 'cancel',
          },
          {
            text: i18n.t('Common.button.login').toString(),
            style: 'submit',
            async onClick() {
              try {
                store.commit('modules/mypage/myshop/setSalesLocation', props.shopDetail.place_code);
                // ログイン画面からマイショップ画面に遷移するため
                $cookies.set(COOKIE_KEY.RETURN_TO, url('MYPAGE_MYSHOP'), { path: '/' });
                await router.push({ path: url('AUTH_LOGIN') });
              } catch {
                return false;
              }
            },
          },
        ],
      });
    };

    // マイショップに登録
    const registerToMyShop = () => {
      if (!authenticated.value) {
        openLoginDialog();
        return;
      }

      store.commit('modules/mypage/myshop/setSalesLocation', props.shopDetail.place_code);
      router.push(url('MYPAGE_MYSHOP'));
    };

    // この店舗のイベント画面へ
    const checkEvents = () => {
      router.push({ path: url('EVENT_LIST'), query: { shop: props.shopDetail.place_code } });
    };

    // この店のスタッフ一覧画面へ
    const checkShopStaff = () => {
      router.push({ path: url('STAFF_LIST'), query: { _shop: props.staffShopId } });
    };

    return {
      isShowAll,
      shopPostNum,
      shopAddress,
      slicedStaffList,
      syncedSelectedAllBlogOption,
      BLOG_SEARCH_ALL_BLOG_OPTION,
      url,
      applyAllBlog,
      applyYearFilter,
      applyMonthFilter,
      registerToMyShop,
      checkEvents,
      checkShopStaff,
    };
  },
});
